<template>
  <div class="edit-panel">
    <!-- Заголовок -->
    <div class="edit-panel__header">
      <span class="edit-panel__title">{{ panelTitle }}</span>
      <button class="edit-panel__close" @click="$emit('close')">✕</button>
    </div>

    <!-- Ошибка -->
    <div v-if="error" class="edit-panel__error">{{ error }}</div>

    <!-- Активная подсказка -->
    <transition name="hint-fade">
      <div v-if="activeHint" class="hint-box">
        <div class="hint-box__header">
          <span class="hint-box__title">{{ activeHint.title }}</span>
          <button class="hint-box__close" @click="activeHint = null">✕</button>
        </div>
        <div class="hint-box__text">{{ activeHint.text }}</div>
        <div class="hint-box__example">{{ activeHint.example }}</div>
      </div>
    </transition>

    <!-- === Редактирование узла === -->
    <template v-if="mode === 'node'">
      <FieldRow label="Название узла *" hint-key="node_label" @hint="showHint">
        <input v-model="nodeForm.node_label" class="field-input" placeholder="Например: Подписать АСР (подрядчик)" />
      </FieldRow>

      <FieldRow label="Тип *" hint-key="node_type" @hint="showHint">
        <select v-model="nodeForm.node_type" class="field-input">
          <option v-for="t in NODE_TYPES" :key="t.value" :value="t.value">
            {{ t.value }} — {{ t.label }}
          </option>
        </select>
      </FieldRow>

      <template v-if="nodeForm.node_type === 'task'">
        <FieldRow label="Роль исполнителя" hint-key="assign_type_code" @hint="showHint">
          <select v-model="nodeForm.assign_type_code" class="field-input">
            <option value="">— не задан —</option>
            <option v-for="r in ASSIGN_ROLES" :key="r.value" :value="r.value">
              {{ r.value }} — {{ r.label }}
            </option>
          </select>
        </FieldRow>

        <FieldRow label="Тип задачи" hint-key="task_type_code" @hint="showHint">
          <select v-model="nodeForm.task_type_code" class="field-input">
            <option value="">— не задан —</option>
            <option v-for="t in TASK_TYPES" :key="t.value" :value="t.value">
              {{ t.value }} — {{ t.label }}
            </option>
          </select>
        </FieldRow>

        <FieldRow label="Тип слияния (join)" hint-key="join_type" @hint="showHint">
          <select v-model="nodeForm.join_type" class="field-input">
            <option value="or">OR — активируется от любой входящей ветки</option>
            <option value="and">AND — ждёт все входящие ветки</option>
          </select>
        </FieldRow>
      </template>

      <template v-if="nodeForm.node_type === 'auto'">
        <FieldRow label="Авто-действие" hint-key="auto_action_code" @hint="showHint">
          <select v-model="nodeForm.auto_action_code" class="field-input">
            <option value="">— не задан —</option>
            <option v-for="a in AUTO_ACTIONS" :key="a.value" :value="a.value">
              {{ a.label }}
            </option>
          </select>
        </FieldRow>
      </template>

      <template v-if="isNew">
        <FieldRow label="Код узла *" hint-key="node_code" @hint="showHint">
          <input v-model="nodeForm.node_code" class="field-input" placeholder="Например: s1_task_sign_asr_new" />
        </FieldRow>
      </template>

      <div class="edit-panel__actions">
        <button class="btn-save" :disabled="saving" @click="saveNode">
          {{ saving ? 'Сохранение...' : isNew ? 'Добавить' : 'Сохранить' }}
        </button>
        <button v-if="!isNew" class="btn-delete" :disabled="saving" @click="deleteNode">
          Удалить
        </button>
      </div>
    </template>

    <!-- === Редактирование ребра === -->
    <template v-else-if="mode === 'edge'">
      <FieldRow label="Подпись ребра" hint-key="edge_label" @hint="showHint">
        <input v-model="edgeForm.edge_label" class="field-input" placeholder="Краткое описание" />
      </FieldRow>

      <FieldRow label="Событие (trigger) *" hint-key="trigger_event" @hint="showHint">
        <select v-model="edgeForm.trigger_event" class="field-input">
          <option v-for="e in TRIGGER_EVENTS" :key="e.value" :value="e.value">
            {{ e.value }} — {{ e.label }}
          </option>
        </select>
      </FieldRow>

      <FieldRow label="Тип документа" hint-key="trigger_doc_type_code" @hint="showHint">
        <select v-model="edgeForm.trigger_doc_type_code" class="field-input">
          <option value="">— не задан —</option>
          <option v-for="d in DOC_TYPES" :key="d.value" :value="d.value">
            {{ d.value }} — {{ d.label }}
          </option>
        </select>
      </FieldRow>

      <FieldRow label="Тип подписанта" hint-key="trigger_assign_type_code" @hint="showHint">
        <select v-model="edgeForm.trigger_assign_type_code" class="field-input">
          <option value="">— не задан —</option>
          <option v-for="r in ASSIGN_ROLES" :key="r.value" :value="r.value">
            {{ r.value }} — {{ r.label }}
          </option>
        </select>
      </FieldRow>

      <FieldRow label="Тип задачи (для TASK_COMPLETED)" hint-key="trigger_task_type_code" @hint="showHint">
        <select v-model="edgeForm.trigger_task_type_code" class="field-input">
          <option value="">— не задан —</option>
          <option v-for="t in TASK_TYPES" :key="t.value" :value="t.value">
            {{ t.value }} — {{ t.label }}
          </option>
        </select>
      </FieldRow>

      <template v-if="isNew">
        <FieldRow label="Узел-источник *" hint-key="source_node_id" @hint="showHint">
          <select v-model="edgeForm.source_node_id" class="field-input">
            <option value="">— выберите узел —</option>
            <option v-for="n in allNodes" :key="n.bpmn_node_id" :value="n.bpmn_node_id">
              {{ n.node_code }} — {{ n.node_label }}
            </option>
          </select>
        </FieldRow>

        <FieldRow label="Узел-цель *" hint-key="target_node_id" @hint="showHint">
          <select v-model="edgeForm.target_node_id" class="field-input">
            <option value="">— выберите узел —</option>
            <option v-for="n in allNodes" :key="n.bpmn_node_id" :value="n.bpmn_node_id">
              {{ n.node_code }} — {{ n.node_label }}
            </option>
          </select>
        </FieldRow>
      </template>

      <!-- Точки подключения — показываем всегда -->
      <div class="handle-row">
        <div class="handle-row__label">Точки подключения</div>
        <div class="handle-row__selects">
          <div class="handle-select-wrap">
            <span class="handle-select-wrap__caption">Из узла</span>
            <div class="handle-picker">
              <button
                v-for="h in HANDLES"
                :key="h.value"
                :class="['hp-btn', `hp-btn--${h.value}`, { 'hp-btn--active': edgeForm.source_handle === h.value }]"
                :title="h.label"
                type="button"
                @click="edgeForm.source_handle = h.value"
              >{{ h.icon }}</button>
            </div>
          </div>
          <span class="handle-row__arrow">→</span>
          <div class="handle-select-wrap">
            <span class="handle-select-wrap__caption">В узел</span>
            <div class="handle-picker">
              <button
                v-for="h in HANDLES"
                :key="h.value"
                :class="['hp-btn', `hp-btn--${h.value}`, { 'hp-btn--active': edgeForm.target_handle === h.value }]"
                :title="h.label"
                type="button"
                @click="edgeForm.target_handle = h.value"
              >{{ h.icon }}</button>
            </div>
          </div>
        </div>
      </div>

      <div class="edit-panel__actions">
        <button class="btn-save" :disabled="saving" @click="saveEdge">
          {{ saving ? 'Сохранение...' : isNew ? 'Добавить' : 'Сохранить' }}
        </button>
        <button v-if="!isNew" class="btn-delete" :disabled="saving" @click="deleteEdge">
          Удалить
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, watch, computed, defineComponent, h } from 'vue'
import { wcheckBpmnAPI } from './services/api'
import { NODE_TYPES, FIELD_HINTS } from './bpmnDicts'

const HANDLES = [
  { value: 'top',    label: 'Сверху',  icon: '↑' },
  { value: 'right',  label: 'Справа',  icon: '→' },
  { value: 'bottom', label: 'Снизу',   icon: '↓' },
  { value: 'left',   label: 'Слева',   icon: '←' },
]
import { useBpmnDicts } from './useBpmnDicts'

const dicts = useBpmnDicts()
const ASSIGN_ROLES    = dicts.assignTypes
const TASK_TYPES      = dicts.taskTypes
const AUTO_ACTIONS    = dicts.autoActions
const TRIGGER_EVENTS  = dicts.triggerEvents
const DOC_TYPES       = dicts.docTypes

// Встроенный компонент строки с меткой и кнопкой ?
const FieldRow = defineComponent({
  props: { label: String, hintKey: String },
  emits: ['hint'],
  setup(props, { slots, emit }) {
    return () => h('div', { class: 'field-group' }, [
      h('div', { class: 'field-group__label-row' }, [
        h('label', props.label),
        props.hintKey && FIELD_HINTS[props.hintKey]
          ? h('button', {
              class: 'hint-btn',
              type: 'button',
              title: 'Подробнее',
              onClick: () => emit('hint', props.hintKey),
            }, '?')
          : null,
      ]),
      slots.default?.(),
    ])
  },
})

const props = defineProps({
  mode: { type: String, default: null },
  item: { type: Object, default: null },
  schemaId: { type: Number, required: true },
  allNodes: { type: Array, default: () => [] },
})

const emit = defineEmits(['close', 'saved', 'deleted'])

const saving = ref(false)
const error = ref(null)
const activeHint = ref(null)

const isNew = computed(() => !props.item?.bpmn_node_id && !props.item?.bpmn_edge_id)

const panelTitle = computed(() => {
  if (props.mode === 'node') return isNew.value ? 'Новый узел' : 'Редактировать узел'
  if (props.mode === 'edge') return isNew.value ? 'Новое ребро' : 'Редактировать ребро'
  return ''
})

function showHint(key) {
  const h = FIELD_HINTS[key]
  if (!h) return
  activeHint.value = activeHint.value?.title === h.title ? null : h
}

const nodeForm = ref({
  node_label: '', node_type: 'task', node_code: '',
  assign_type_code: '', task_type_code: '', auto_action_code: '',
  join_type: 'or',
})

const edgeForm = ref({
  edge_label: '', trigger_event: null,
  trigger_doc_type_code: '', trigger_assign_type_code: '',
  trigger_task_type_code: '', source_node_id: '', target_node_id: '',
  source_handle: 'right', target_handle: 'left',
})

watch(() => props.item, (val) => {
  error.value = null
  activeHint.value = null
  if (!val) return
  if (props.mode === 'node') {
    nodeForm.value = {
      node_label: val.node_label || '',
      node_type: val.node_type || 'task',
      node_code: val.node_code || '',
      assign_type_code: val.assign_type_code || '',
      task_type_code: val.task_type_code || '',
      auto_action_code: val.auto_action_code || '',
      join_type: val.join_type || 'or',
    }
  } else if (props.mode === 'edge') {
    edgeForm.value = {
      edge_label: val.edge_label || '',
      trigger_event: val.trigger_event || null,
      trigger_doc_type_code: val.trigger_doc_type_code || '',
      trigger_assign_type_code: val.trigger_assign_type_code || '',
      trigger_task_type_code: val.trigger_task_type_code || '',
      source_node_id: val.source_node_id || '',
      target_node_id: val.target_node_id || '',
      source_handle: val.source_handle || 'right',
      target_handle: val.target_handle || 'left',
    }
  }
}, { immediate: true })

function n(v) { return v === '' || v === undefined ? null : v }

async function saveNode() {
  error.value = null
  saving.value = true
  try {
    const f = nodeForm.value
    if (isNew.value) {
      await wcheckBpmnAPI.createNode({
        schema_id: props.schemaId,
        node_type: f.node_type, node_code: f.node_code, node_label: f.node_label,
        assign_type_code: n(f.assign_type_code), task_type_code: n(f.task_type_code),
        auto_action_code: n(f.auto_action_code),
        join_type: f.join_type || 'or',
        pos_x: 600, pos_y: 400,
      })
    } else {
      await wcheckBpmnAPI.updateNode({
        bpmn_node_id: props.item.bpmn_node_id,
        node_label: f.node_label,
        assign_type_code: n(f.assign_type_code), task_type_code: n(f.task_type_code),
        auto_action_code: n(f.auto_action_code),
        join_type: f.join_type || 'or',
      })
    }
    if (isNew.value) {
      emit('saved', { type: 'node', isNew: true })
    } else {
      const f = nodeForm.value
      emit('saved', {
        type: 'node', isNew: false,
        data: {
          bpmn_node_id:      props.item.bpmn_node_id,
          node_label:        f.node_label,
          node_type:         f.node_type,
          node_code:         props.item.node_code,
          assign_type_code:  n(f.assign_type_code),
          task_type_code:    n(f.task_type_code),
          auto_action_code:  n(f.auto_action_code),
          join_type:         f.join_type || 'or',
        },
      })
    }
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || 'Ошибка сохранения'
  } finally {
    saving.value = false
  }
}

async function deleteNode() {
  if (!confirm(`Удалить узел "${props.item.node_label}"?\nСначала удалите все рёбра, связанные с ним.`)) return
  error.value = null
  saving.value = true
  try {
    await wcheckBpmnAPI.deleteNode(props.item.bpmn_node_id)
    emit('deleted', { type: 'node', id: props.item.bpmn_node_id })
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || 'Ошибка удаления'
  } finally {
    saving.value = false
  }
}

async function saveEdge() {
  error.value = null
  saving.value = true
  try {
    const f = edgeForm.value
    if (isNew.value) {
      await wcheckBpmnAPI.createEdge({
        schema_id: props.schemaId,
        source_node_id: f.source_node_id, target_node_id: f.target_node_id,
        source_handle: f.source_handle || 'right',
        target_handle: f.target_handle || 'left',
        trigger_event: f.trigger_event,
        trigger_doc_type_code: n(f.trigger_doc_type_code),
        trigger_assign_type_code: n(f.trigger_assign_type_code),
        trigger_task_type_code: n(f.trigger_task_type_code),
        edge_label: n(f.edge_label),
      })
    } else {
      await wcheckBpmnAPI.updateEdge({
        bpmn_edge_id: props.item.bpmn_edge_id,
        source_node_id: props.item.source_node_id,
        target_node_id: props.item.target_node_id,
        source_handle: f.source_handle || 'right',
        target_handle: f.target_handle || 'left',
        trigger_event: f.trigger_event,
        trigger_doc_type_code: n(f.trigger_doc_type_code),
        trigger_assign_type_code: n(f.trigger_assign_type_code),
        trigger_task_type_code: n(f.trigger_task_type_code),
        edge_label: n(f.edge_label),
      })
    }
    if (isNew.value) {
      emit('saved', { type: 'edge', isNew: true })
    } else {
      emit('saved', {
        type: 'edge', isNew: false,
        data: {
          bpmn_edge_id:              props.item.bpmn_edge_id,
          source_node_id:            props.item.source_node_id,
          target_node_id:            props.item.target_node_id,
          source_handle:             f.source_handle || 'right',
          target_handle:             f.target_handle || 'left',
          trigger_event:             f.trigger_event,
          trigger_doc_type_code:     n(f.trigger_doc_type_code),
          trigger_assign_type_code:  n(f.trigger_assign_type_code),
          trigger_task_type_code:    n(f.trigger_task_type_code),
          edge_label:                n(f.edge_label),
        },
      })
    }
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || 'Ошибка сохранения'
  } finally {
    saving.value = false
  }
}

async function deleteEdge() {
  if (!confirm('Удалить это ребро?')) return
  error.value = null
  saving.value = true
  try {
    await wcheckBpmnAPI.deleteEdge(props.item.bpmn_edge_id)
    emit('deleted', { type: 'edge', id: props.item.bpmn_edge_id })
  } catch (e) {
    error.value = e?.response?.data?.error || e?.message || 'Ошибка удаления'
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.edit-panel {
  width: 320px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  max-height: 600px;
  overflow-y: auto;
}

.edit-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.edit-panel__title {
  font-size: 14px;
  font-weight: 700;
  color: #212121;
}

.edit-panel__close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #757575;
  padding: 2px 6px;
  border-radius: 4px;
}
.edit-panel__close:hover { background: #f5f5f5; }

.edit-panel__error {
  background: #ffebee;
  color: #c62828;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
}

/* Подсказка */
.hint-box {
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 12px;
  color: #1b5e20;
}

.hint-box__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.hint-box__title {
  font-weight: 700;
  font-size: 13px;
}

.hint-box__close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: #388e3c;
}

.hint-box__text {
  white-space: pre-line;
  line-height: 1.5;
  margin-bottom: 4px;
}

.hint-box__example {
  font-style: italic;
  color: #2e7d32;
  border-top: 1px solid #c8e6c9;
  padding-top: 4px;
  margin-top: 4px;
}

.hint-fade-enter-active,
.hint-fade-leave-active { transition: opacity 0.2s; }
.hint-fade-enter-from,
.hint-fade-leave-to { opacity: 0; }

/* Поля */
.field-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-group__label-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.field-group__label-row label {
  font-size: 11px;
  font-weight: 600;
  color: #546e7a;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex: 1;
}

.hint-btn {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid #90caf9;
  background: #e3f2fd;
  color: #1565c0;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hint-btn:hover { background: #bbdefb; }

.field-input {
  padding: 7px 10px;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  font-size: 13px;
  color: #212121;
  background: #fafafa;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
}
.field-input:focus {
  border-color: #1976d2;
  background: #fff;
}

.edit-panel__actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.btn-save {
  flex: 1;
  padding: 8px;
  background: #1976d2;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}
.btn-save:hover:not(:disabled) { background: #1565c0; }
.btn-save:disabled { opacity: 0.6; cursor: default; }

.btn-delete {
  padding: 8px 14px;
  background: #ffebee;
  color: #c62828;
  border: 1px solid #ef9a9a;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
}
.btn-delete:hover:not(:disabled) { background: #ffcdd2; }
.btn-delete:disabled { opacity: 0.6; cursor: default; }

/* Пикер точки подключения */
.handle-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.handle-row__label {
  font-size: 11px;
  font-weight: 600;
  color: #546e7a;
  text-transform: uppercase;
  letter-spacing: .3px;
}
.handle-row__selects {
  display: flex;
  align-items: center;
  gap: 8px;
}
.handle-row__arrow {
  font-size: 16px;
  color: #90a4ae;
  flex-shrink: 0;
}
.handle-select-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1;
}
.handle-select-wrap__caption {
  font-size: 10px;
  color: #90a4ae;
}
.handle-picker {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  width: 100%;
}
.hp-btn {
  padding: 5px 0;
  border-radius: 5px;
  border: 1.5px solid #cfd8dc;
  background: #f5f9ff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  color: #78909c;
  transition: all .15s;
  text-align: center;
}
.hp-btn:hover { border-color: #90caf9; color: #1976d2; background: #e3f2fd; }
.hp-btn--active {
  border-color: #1976d2;
  background: #1976d2;
  color: #fff;
}
</style>
